import { useGeckoPermissions } from 'features/permissions'
import { X } from 'lucide-react'
import React, { useRef, useState } from 'react'
import { createDefaultEntry, FormEntry, SymbolType } from 'types'

import {
  CellCheckbox,
  CellInput,
  CellLabel,
  CellTextarea,
  Checkbox,
  CheckboxSublabel,
  ClearButton,
  DateInputWrapper,
  EmptyFormState,
  EmptyStateIcon,
  EmptyStateSubtext,
  FormCell,
  FormCellNarrow,
  FormContent,
  FormHeader,
  FormPanel,
  FormRow,
  FormTable,
  FormTitle,
  SplitCell,
  SplitCellContent,
  SplitCellHeader,
  SplitRow,
  SymbolButton,
  SymbolButtonGroup,
} from './DiscrepancyForm.styles'

interface DiscrepancyFormProps {
  entry: FormEntry | null
  entryIndex: number | null
  onEntryChange: (updatedEntry: FormEntry) => void
}

const SYMBOL_OPTIONS: { value: SymbolType; display: string }[] = [
  { value: 'red-x', display: '✕' },
  { value: 'red-dash', display: '—' },
  { value: 'red-diagonal', display: '/' },
]

export const DiscrepancyForm: React.FC<DiscrepancyFormProps> = ({
  entry: initialEntry,
  entryIndex,
  onEntryChange,
}) => {
  const { user } = useGeckoPermissions()

  // Single ref to track current entry state - all inputs are uncontrolled. This avoids laggy keystroke issues in text inputs.
  const entryRef = useRef<FormEntry>(initialEntry || createDefaultEntry())

  // Symbol needs state because it's a styled button group that needs re-render for visual feedback
  const [sym, setSym] = useState<SymbolType>(initialEntry?.sym || '')

  // Track whether date corrected has a value (for showing clear button)
  const [hasDateCorrected, setHasDateCorrected] = useState(
    !!initialEntry?.dateCorrected
  )

  // Ref to imperatively clear date corrected input
  const dateCorrectedRef = useRef<HTMLInputElement>(null)

  if (entryIndex === null || !initialEntry) {
    return (
      <FormPanel>
        <EmptyFormState>
          <EmptyStateIcon>📋</EmptyStateIcon>
          <div>Select an entry from the list</div>
          <EmptyStateSubtext>
            to view and edit its form fields
          </EmptyStateSubtext>
        </EmptyFormState>
      </FormPanel>
    )
  }

  // Generic handler for all uncontrolled fields
  const handleChange = (
    field: keyof FormEntry,
    value: FormEntry[keyof FormEntry]
  ) => {
    entryRef.current = { ...entryRef.current, [field]: value }
    onEntryChange(entryRef.current)
  }

  // Symbol needs state update for button styling
  const handleSymChange = (newSym: SymbolType) => {
    setSym(newSym)
    handleChange('sym', newSym)
  }

  // Date corrected needs to track value presence for clear button visibility
  const handleDateCorrectedChange = (value: string) => {
    setHasDateCorrected(!!value)
    handleChange('dateCorrected', value)
  }

  const handleClearDateCorrected = () => {
    if (dateCorrectedRef.current) {
      dateCorrectedRef.current.value = ''
    }
    setHasDateCorrected(false)
    handleChange('dateCorrected', '')
  }

  const entryTitle = initialEntry.annotationLabel || `Entry ${entryIndex + 1}`

  return (
    <FormPanel>
      <FormHeader>
        <div>
          <FormTitle>{entryTitle}</FormTitle>
        </div>
      </FormHeader>

      <FormContent>
        <FormTable>
          <FormRow>
            <FormCell>
              <CellLabel>SYM</CellLabel>
              <SymbolButtonGroup>
                {SYMBOL_OPTIONS.map((opt) => (
                  <SymbolButton
                    key={opt.value}
                    type='button'
                    $selected={sym === opt.value}
                    onClick={() =>
                      handleSymChange(sym === opt.value ? '' : opt.value)
                    }>
                    {opt.display}
                  </SymbolButton>
                ))}
              </SymbolButtonGroup>
            </FormCell>

            <FormCell>
              <CellLabel>JCN</CellLabel>
              <CellInput
                type='text'
                defaultValue={initialEntry.jcn}
                onChange={(e) => handleChange('jcn', e.target.value)}
                placeholder='Enter'
              />
            </FormCell>

            <FormCell>
              <CellLabel>DATE DISC</CellLabel>
              <CellInput
                type='date'
                defaultValue={initialEntry.dateDisc}
                onChange={(e) => handleChange('dateDisc', e.target.value)}
              />
            </FormCell>

            <FormCell>
              <CellLabel>DOC NO.</CellLabel>
              <CellInput
                type='text'
                defaultValue={initialEntry.docNo}
                onChange={(e) => handleChange('docNo', e.target.value)}
                placeholder='Enter'
              />
            </FormCell>

            <FormCellNarrow>
              <CellCheckbox>
                <CellLabel>CF</CellLabel>
                <Checkbox
                  type='checkbox'
                  defaultChecked={initialEntry.cf}
                  onChange={(e) => handleChange('cf', e.target.checked)}
                />
                <CheckboxSublabel>781A</CheckboxSublabel>
              </CellCheckbox>
            </FormCellNarrow>

            <FormCellNarrow>
              <CellCheckbox>
                <CellLabel>XF</CellLabel>
                <Checkbox
                  type='checkbox'
                  defaultChecked={initialEntry.xf}
                  onChange={(e) => handleChange('xf', e.target.checked)}
                />
                <CheckboxSublabel>781K</CheckboxSublabel>
              </CellCheckbox>
            </FormCellNarrow>

            <FormCell>
              <CellLabel>DATE CORRECTED</CellLabel>
              <DateInputWrapper>
                <CellInput
                  ref={dateCorrectedRef}
                  type='date'
                  defaultValue={initialEntry.dateCorrected}
                  onChange={(e) => handleDateCorrectedChange(e.target.value)}
                />
                {hasDateCorrected && (
                  <ClearButton
                    type='button'
                    onClick={handleClearDateCorrected}
                    title='Clear date'>
                    <X />
                  </ClearButton>
                )}
              </DateInputWrapper>
            </FormCell>
          </FormRow>

          <SplitRow $minHeight='200px'>
            <SplitCell>
              <FormRow>
                <FormCell>
                  <CellLabel>WUC/REF DESIGNATOR</CellLabel>
                  <CellInput
                    type='text'
                    defaultValue={initialEntry.wucRef}
                    onChange={(e) => handleChange('wucRef', e.target.value)}
                    placeholder='Enter'
                  />
                </FormCell>
                <FormCell>
                  <CellLabel>FAULT CODE</CellLabel>
                  <CellInput
                    type='text'
                    defaultValue={initialEntry.faultCode}
                    onChange={(e) => handleChange('faultCode', e.target.value)}
                    placeholder='Enter'
                  />
                </FormCell>
                <FormCell $noBorder>
                  <CellLabel>STA CODE</CellLabel>
                  <CellInput
                    type='text'
                    defaultValue={initialEntry.staCode}
                    onChange={(e) => handleChange('staCode', e.target.value)}
                    placeholder='Enter'
                  />
                </FormCell>
              </FormRow>

              <SplitCellHeader>DISCREPANCY</SplitCellHeader>
              <SplitCellContent>
                <CellTextarea
                  defaultValue={initialEntry.discrepancyText}
                  onChange={(e) =>
                    handleChange('discrepancyText', e.target.value)
                  }
                  placeholder='Enter discrepancy details...'
                />
              </SplitCellContent>
            </SplitCell>

            <SplitCell>
              <SplitCellHeader>CORRECTIVE ACTION</SplitCellHeader>
              <SplitCellContent>
                <CellTextarea
                  defaultValue={initialEntry.correctiveAction}
                  onChange={(e) =>
                    handleChange('correctiveAction', e.target.value)
                  }
                  placeholder='Enter corrective action...'
                />
              </SplitCellContent>

              <FormRow $noBorder>
                <FormCell>
                  <CellLabel>CORRECTED BY</CellLabel>
                  <CellInput
                    type='text'
                    defaultValue={initialEntry.correctedBy}
                    onChange={(e) =>
                      handleChange('correctedBy', e.target.value)
                    }
                    placeholder='Enter name'
                  />
                </FormCell>
                <FormCell>
                  <CellLabel>EMPLOYEE NO.</CellLabel>
                  <CellInput
                    type='text'
                    defaultValue={initialEntry.correctedByEmployeeNo}
                    onChange={(e) =>
                      handleChange('correctedByEmployeeNo', e.target.value)
                    }
                    placeholder='Enter'
                  />
                </FormCell>
              </FormRow>
            </SplitCell>
          </SplitRow>

          <FormRow>
            <FormCell>
              <CellLabel>DISCOVERED BY (Print)</CellLabel>
              <CellInput
                type='text'
                defaultValue={initialEntry.discoveredBy}
                onChange={(e) => handleChange('discoveredBy', e.target.value)}
                placeholder='Enter name'
              />
            </FormCell>

            <FormCell>
              <CellLabel>EMPLOYEE NO.</CellLabel>
              <CellInput
                type='text'
                defaultValue={initialEntry.discoveredByEmployeeNo}
                onChange={(e) =>
                  handleChange('discoveredByEmployeeNo', e.target.value)
                }
                placeholder='Enter'
              />
            </FormCell>

            <FormCell>
              <CellLabel>INSPECTED BY</CellLabel>
              <CellInput type='text' value={user?.email || ''} disabled />
            </FormCell>

            <FormCell>
              <CellLabel>EMPLOYEE NO.</CellLabel>
              <CellInput
                type='text'
                defaultValue={initialEntry.inspectedByEmployeeNo}
                onChange={(e) =>
                  handleChange('inspectedByEmployeeNo', e.target.value)
                }
                placeholder='Enter'
              />
            </FormCell>
          </FormRow>
        </FormTable>
      </FormContent>
    </FormPanel>
  )
}
